import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  signInWithPopup,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { FiDollarSign, FiUser } from 'react-icons/fi';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import {
  RootState,
  loginSuccess,
  useLazyGetUserByFirebaseIdQuery,
  useGetEmailInvitationsQuery,
  useSendRecoverPasswordEmailMutation,
  useUseScannerKeyMutation,
} from '../store';
import { auth, googleAuth } from '../config/firebase-config';
import config from '../config/config';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import UserDTO from '../models/user';
import CustomModal from '../components/atoms/CustomModal';
import WhatsappFloater from '../components/WhatsappFloater';
import AvanttiIcon from '../components/icons/AvanttiIcon';
import QrCode02Icon from '../components/icons/QrCode02Icon';

interface FormData {
  email: string;
  password: string;
}

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .required('Debes ingresar un correo')
    .email('Este correo no está bien'),
  password: yup.string().required('Debes ingresar una contraseña'),
});

const SCANNER_KEY_STORAGE_KEY = 'scannerKey';

const LogIn: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const [authState, setAuthState] = useState({ token: '', firebaseId: '' });
  const [firebaseAuthLoading, setFirebaseAuthLoading] = useState(false);
  const [getUserByFirebaseId] = useLazyGetUserByFirebaseIdQuery();
  const [user, setUser] = useState<UserDTO | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [firstTimeParam, setFirstTimeParam] = useState(false);
  const [sendRecoverPasswordEmail] = useSendRecoverPasswordEmailMutation();
  const [isLoadingRecoverPassword, setIsLoadingRecoverPassword] = useState(false);
  const [useScannerKey] = useUseScannerKeyMutation();
  const [scannerKey, setscannerKey] = useState<string | null>(null);
  const scannerKeyProcessedRef = useRef(false);

  // Helper function to get step from URL
  const getStepFromUrl = (): 'select' | 'producer' | 'seller' | 'scanner' | 'forgot' => {
    const searchParams = new URLSearchParams(location.search);
    const stepParam = searchParams.get('step');
    if (stepParam === 'producer' || stepParam === 'seller' || stepParam === 'scanner' || stepParam === 'forgot') {
      return stepParam;
    }
    return 'select';
  };
  const { user: stateUser, token: stateToken } = useSelector(
    (s: RootState) => s.auth,
  );
  const [isLoading, setIsLoading] = useState(false);

  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const form = useForm<FormData>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const recoverPasswordForm = useForm<{ email: string }>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(yup.object().shape({
      email: yup
        .string()
        .required('Debes ingresar un correo')
        .email('Este correo no está bien'),
    })),
  });

  // Store scannerKey from URL if present or recover from sessionStorage (for Google redirects)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('scannerKey');

    if (key) {
      setscannerKey(key);
      sessionStorage.setItem(SCANNER_KEY_STORAGE_KEY, key);
      return;
    }

    const storedKey = sessionStorage.getItem(SCANNER_KEY_STORAGE_KEY);
    if (storedKey) {
      setscannerKey(storedKey);
    }
  }, [location.search]);

  useEffect(() => {
    const currentUser = user ?? (stateUser?.id ? stateUser : null);
    const currentToken = authState.token || stateToken;

    if (currentUser && currentToken && scannerKey && currentUser.id && !scannerKeyProcessedRef.current) {
      console.log('Processing scanner key:', scannerKey, 'for user:', currentUser.id);
      scannerKeyProcessedRef.current = true;
      useScannerKey({ key: scannerKey, user_id: currentUser.id });
    }
  }, [user, stateUser, authState.token, stateToken, scannerKey, useScannerKey]);

  useEffect(() => {
    if (user && authState.token) {
      dispatch(
        loginSuccess({
          user: {
            id: user.id,
            username: user.username,
            type: user.type,
            email: user.email,
            phone_number: user.phone_number,
            user_businesses: user.user_businesses,
            user_events: user.user_events,
            photo_url: user.photo_url,
          },
          token: authState.token,
        }),
      );

      setIsLoading(false);
    }
  }, [user, authState.token, dispatch]);

  // Query invitations for seller/scanner users
  const step = getStepFromUrl();
  const { data: invitations, isLoading: isLoadingInvitations } = useGetEmailInvitationsQuery(
    { email: stateUser?.email || '' },
    { skip: !stateUser?.email || (step !== 'seller' && step !== 'scanner') },
  );

  const getRedirectBasedOnUser = () => {
    const currentStep = getStepFromUrl();

    if (currentStep === 'producer') {
      return '/admin/businesses';
    }
    if (currentStep === 'seller' || currentStep === 'scanner') {
      // Check if user has pending invitations
      const pendingInvitations = invitations?.filter((inv) => inv.status === 'pending');
      if (pendingInvitations && pendingInvitations.length > 0) {
        return '/admin/invitations';
      }
      return '/admin/businesses';
    }
    if (location.state?.from) {
      return location.state?.from;
    }
    return '/admin/businesses';
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const firstTime = searchParams.get('firstTime');
    setFirstTimeParam(firstTime === 'true');
    if (stateToken && stateUser && firstTimeParam) {
      setIsModalOpen(true);
    } else if (stateToken && stateUser) {
      setIsLoading(false);
      // Wait for invitations to load if seller/scanner
      if ((step === 'seller' || step === 'scanner') && isLoadingInvitations) {
        return; // Wait for invitations query to complete
      }
      navigate(getRedirectBasedOnUser());
    }
  }, [stateToken, stateUser, location.state?.firstTime, location.search, step, invitations, isLoadingInvitations, navigate]);

  const { register, handleSubmit, formState } = form;

  const handleSignInWithGoogle = async () => {
    try {
      setFirebaseAuthLoading(true);
      if (config.env !== 'local') {
        // Add step to URL before redirecting
        const currentStep = getStepFromUrl();
        const url = new URL(window.location.href);
        url.searchParams.set('step', currentStep);
        window.history.replaceState({}, '', url.toString());
        return await signInWithRedirect(auth, googleAuth);
      }
      // local no funciona redirect
      const { user: fbUser } = await signInWithPopup(auth, googleAuth);
      const token = await fbUser.getIdToken();
      const firebaseId = fbUser.uid;

      if (!firebaseId) {
        throw new Error('Failed to get Firebase ID');
      }
      if (!fbUser.email) {
        throw new Error('Google User has no email');
      }
      if (fbUser.uid) {
        const { data: userByFbId } = await getUserByFirebaseId({ firebaseId: fbUser.uid });
        setUser(userByFbId ?? null);
      }

      return setAuthState({ token, firebaseId });
    } catch (e) {
      console.error(e);
      return setFirebaseAuthLoading(false);
    }
  };

  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        setFirebaseAuthLoading(true);
        const result = await getRedirectResult(auth);
        if (result?.user) {
          const fbUser = result.user;
          const token = await fbUser.getIdToken();
          const firebaseId = fbUser.uid;

          if (!firebaseId) {
            throw new Error('Failed to get Firebase ID');
          }
          if (!fbUser.email) {
            throw new Error('Google User has no email');
          }

          // Fetch the user data like we do in handleSignInWithGoogle
          if (firebaseId) {
            const { data: userByFbId } = await getUserByFirebaseId({ firebaseId });
            setUser(userByFbId ?? null);
          }

          setAuthState({ token, firebaseId });
        }
      } catch (e) {
        console.error('Redirect error:', e);
      } finally {
        setFirebaseAuthLoading(false);
      }
    };

    checkRedirectResult();
  }, [getUserByFirebaseId]);

  const handleLogIn = async (formData: FormData) => {
    setFirebaseAuthLoading(true);
    setErrorMsg('');
    setIsLoading(true);

    try {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (storageError) {
        try {
          await setPersistence(auth, browserSessionPersistence);
        } catch (sessionError) {
          await setPersistence(auth, inMemoryPersistence);
          console.warn('Using in-memory persistence - user will need to login again after closing the app');
        }
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password,
      );

      const token = await userCredential.user.getIdToken();
      const firebaseId = userCredential.user.uid;

      if (firebaseId) {
        const { data: avanttiUser } = await getUserByFirebaseId({ firebaseId });
        setUser(avanttiUser ?? null);
      }

      if (!firebaseId) {
        throw new Error('Failed to get Firebase ID');
      }

      setAuthState({ token, firebaseId });
    } catch (e) {
      console.error('Login error:', e);
      if (e instanceof FirebaseError && e.code === 'auth/invalid-credential') {
        setErrorMsg('Usuario o contraseña incorrectos');
      } else if (e instanceof Error) {
        setErrorMsg(`Ocurrió un error: ${e.message}`);
      } else {
        setErrorMsg('Ocurrió un error desconocido');
      }
    } finally {
      setIsLoading(false);
      setFirebaseAuthLoading(false);
    }
  };

  const handleRecoverPassword = async (formData: { email: string }) => {
    setErrorMsg('');
    setSuccessMsg('');
    setIsLoadingRecoverPassword(true);
    try {
      await sendRecoverPasswordEmail(formData).unwrap();
      setSuccessMsg(`Se ha enviado un correo electrónico a ${formData.email}`);
      setIsLoadingRecoverPassword(false);
    } catch (e) {
      console.error('Recover password error:', e);
      const error = e as { data?: { message?: string }; message?: string };
      if (error?.data?.message) {
        setErrorMsg(error.data.message);
      } else if (error?.message) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg('Ocurrió un error al enviar el correo. Por favor, intenta nuevamente.');
      }
      setIsLoadingRecoverPassword(false);
    }
  };

  return (
    <div className='min-h-screen'>
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
      >
        <div className="flex flex-col gap-4 max-w-[358px]">
          <h1>¡Inicio de sesión correcto!</h1>
          <p>Para comenzar vamos a crear tu productora, para que luego puedas gestionar tu evento, entradas, asistentes, cupones, y más.</p>
          <p>Actualmente no tenés productoras asociadas.</p>
          <div className='flex flex-row gap-4'>
            <Button variant="secondary" onClick={() => { setIsModalOpen(false); navigate('/admin'); }}>Omitir</Button>
            <Button variant="primary" onClick={() => { setIsModalOpen(false); navigate('/admin/onboarding/create-business'); }}>Continuar</Button>
          </div>
        </div>
      </CustomModal>
      {firstTimeParam && <WhatsappFloater />}

      {step === 'select' && (
        <div className="flex flex-col items-center admin p-6 my-10 rounded-3xl bg-blur bg-opacity-heavy shadow-sm max-w-[820px] mx-auto">
          <div className="flex flex-col items-center mt-10">
            <div className="h-20 w-64">
              <AvanttiIcon className="w-full w-24 h-24 text-text-primary" />
            </div>
            <h1 className="text-2xl mt-8">Iniciar sesión</h1>
            <p className="text-center mb-6">Seleccioná el tipo de usuario que sos</p>
          </div>
          <div>
            <div className="flex flex-col md:flex-row gap-6 justify-center items-stretch my-4">
              <div
                onClick={() => navigate('/login?step=producer')}
                className="flex flex-1 flex-col items-center gap-3 cursor-pointer group hover:scale-105 transition-transform duration-200 min-w-0"
              >
                <div className="flex w-full flex-col items-center gap-3 p-4 rounded-3xl bg-blur bg-opacity-medium border-2 border-border-primary items-center justify-center group-hover:border-primary-500 transition-colors duration-200 shadow-md">
                  <FiUser className="text-text-primary w-10 h-10" />
                  <p className="text-text-primary font-medium">Productor</p>
                  <p className="text-text-tertiary text-sm text-center">Si sos productor, coproductor o editor, ingresá con esta opción.</p>
                </div>
              </div>
              <div
                onClick={() => navigate('/login?step=seller')}
                className="flex flex-1 flex-col items-center gap-3 cursor-pointer group hover:scale-105 transition-transform duration-200 min-w-0"
              >
                <div className="flex w-full flex-col items-center gap-3 p-4 rounded-3xl bg-blur bg-opacity-medium border-2 border-border-primary items-center justify-center group-hover:border-primary-500 transition-colors duration-200 shadow-md">
                  <div className="relative inline-block">
                    <FiUser className="text-text-primary w-10 h-10" />
                    <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full">
                      <FiDollarSign className="text-text-primary w-4 h-4" />
                    </div>
                  </div>
                  <p className="text-text-primary font-medium">Vendedor</p>
                  <p className="text-text-tertiary text-sm text-center">Si sos vendedor, ingresá con esta opción.</p>
                </div>
              </div>
              <div
                onClick={() => navigate('/login?step=scanner')}
                className="flex flex-1 flex-col items-center gap-3 cursor-pointer group hover:scale-105 transition-transform duration-200 min-w-0"
              >
                <div className="flex w-full flex-col items-center gap-3 p-4 rounded-3xl bg-blur bg-opacity-medium border-2 border-border-primary items-center justify-center group-hover:border-primary-500 transition-colors duration-200 shadow-md">
                  <div className="relative inline-block">
                    <FiUser className="text-text-primary w-10 h-10" />
                    <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full">
                      <QrCode02Icon className="text-text-primary w-4 h-4" />
                    </div>
                  </div>
                  <p className="text-text-primary font-medium">Escáner</p>
                  <p className="text-text-tertiary text-sm text-center">Si sos escáner, ingresá con esta opción.</p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-text-tertiary mt-6 mb-10">¿No tenés una cuenta? <p onClick={() => navigate('/signup')} className="text-primary-500 font-medium cursor-pointer">Registrate</p></p>
        </div>)}
      {(step === 'producer' || step === 'seller' || step === 'scanner') && (
        <div className="flex flex-col items-center admin p-6 my-10 rounded-3xl bg-blur bg-opacity-heavy shadow-sm max-w-[820px] mx-auto">
          <div className="flex flex-col items-center mt-10">
            <div className="h-20 w-64">
              <AvanttiIcon className="w-full w-24 h-24 text-text-primary" />
            </div>
            <h1 className="text-2xl mt-8">Iniciar sesión</h1>
            <p className="text-center mb-6"> Ingresa tus datos para acceder al sistema</p>
          </div>
          <div className="flex justify-center align-items-center mb-6 w-full max-w-[358px]">
            <Button
              loading={firebaseAuthLoading}
              disabled={firebaseAuthLoading}
              className="text-lg"
              type="button"
              onClick={handleSignInWithGoogle}
              variant="secondary"
            >
              <div className="flex items-center align-items-center">
                <img src="/google-icon.svg" className="w-5 h-5 mr-2" />
                <span>Ingresar con Google</span>
              </div>
            </Button>
          </div>
          <div className="flex justify-center w-full mb-6">
            <div className="flex items-center gap-4 w-full max-w-[358px]">
              <hr className="flex-grow border-border-primary" />
              <span className="text-text-tertiary">o</span>
              <hr className="flex-grow border-border-primary" />
            </div>
          </div>
          <form className="flex flex-col gap-6 w-full max-w-[358px]" onSubmit={handleSubmit(handleLogIn)}>
            <Input
              label="Correo electrónico"
              labelAbove={true}
              id="email"
              {...register('email', {
                required: true,
              })}
              errMsg={formState.errors.email?.message}
            />
            <Input
              label="Contraseña"
              labelAbove={true}
              id="password"
              type="password"
              {...register('password', {
                required: true,
              })}
              errMsg={formState.errors.password?.message}
            />
            <p className="text-sm text-text-tertiary my-[-16px] flex ">¿Olvidaste tu contraseña? <p onClick={() => navigate('/login?step=forgot')} className="text-[#6941C6] font-medium cursor-pointer ml-1">Recuperar</p></p>
            {errorMsg && <p className="text-error-600">{errorMsg}</p>}
            <div className="flex justify-center mt-2 w-full max-w-[358px]">
              <Button
                loading={isLoading}
                className="text-lg"
                type="submit"
                variant="primary"
              >
                Ingresar
              </Button>
            </div>
          </form>

          <p className="text-center text-sm text-text-tertiary mt-6 mb-10">¿No tenés una cuenta? <p onClick={() => navigate('/signup')} className="text-[#6941C6] font-medium cursor-pointer">Registrate</p></p>
        </div>
      )}
      {step === 'forgot' && (
        <div className="flex flex-col items-center admin p-6 my-10 rounded-3xl bg-blur bg-opacity-heavy shadow-sm max-w-[820px] mx-auto">
          <div className="flex flex-col items-center mt-10">
            <div className="h-20 w-64">
              <AvanttiIcon className="w-full w-24 h-24 text-text-primary" />
            </div>
            <h1 className="text-2xl mt-8">Recuperar contraseña</h1>
            <p className="text-center mb-6"> Ingresa tu correo electrónico para recuperar tu contraseña</p>
            <form className="flex flex-col gap-6 w-full max-w-[358px]" onSubmit={recoverPasswordForm.handleSubmit(handleRecoverPassword)}>
              <Input
                label="Correo electrónico"
                labelAbove={true}
                id="recover-email"
                {...recoverPasswordForm.register('email')}
                errMsg={recoverPasswordForm.formState.errors.email?.message}
              />
              {successMsg && <p className="text-success-600">{successMsg}</p>}
              {errorMsg && <p className="text-error-600">{errorMsg}</p>}
              <div className="flex flex-row gap-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/login')}>
                  Volver
                </Button>
                <Button variant="primary" type="submit" loading={isLoadingRecoverPassword}>
                  Recuperar
                </Button>
              </div>
            </form>
          </div>
          <p className="text-center text-sm text-text-tertiary mt-6 mb-10">¿No tenés una cuenta? <p onClick={() => navigate('/signup')} className="text-[#6941C6] font-medium cursor-pointer">Registrate</p></p>
        </div>
      )}
    </div>
  );
};
export default LogIn;
